import {
    Link,
    useFetcher,
    useNavigation,
    useSearchParams,
} from '@remix-run/react';
import {
    useStyles,
    Grid,
    Flex,
    Button,
    Header,
    HotButtons,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import * as S from './FeaturedProducts.style';
import ProductTile from '~/modules/tile/component/product-tile/ProductTile';
import type ProductTileItem from '~/modules/tile/type/product-tile-item';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useTracker } from '@archipro-website/tracker';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { getHotButtons } from '~/modules/directory/util/get-hot-buttons';
import { getAdminCaption } from '~/modules/directory/util/get-admin-caption';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import AdminCaption from '~/modules/tile/component/admin-caption/AdminCaption';
import { useEffect, useState } from 'react';
import { useToggleHomePin } from '~/modules/home/hook/use-toggle-home-pin';
import MessageModal from '~/modules/home/component/common/MessageModal';
import { canHomePin } from '~/modules/home/util/home-pin-helpers';
import { fetcherIsDone } from '~/utils/fetcherHelper';

interface ProjectProductTile extends ProductTileItem {
    Professional: {
        ID: number;
        Title: string;
        Link: string;
    };
}

export interface FeaturedProductsData {
    Title: string;
    FeaturedProducts: {
        pageInfo: {
            totalCount: number;
        };
        edges: {
            node: ProjectProductTile;
        }[];
    };
}

interface FeaturedProductsProps {
    data: FeaturedProductsData;
    parentID?: number;
}

const ROW_SIZE = 5;
export const DEFAULT_TILE_LIMIT = ROW_SIZE;
export const SHOW_ALL_PARAM = 'featured-products-show-all';

const FeaturedProducts = ({
    parentID,
    data: { Title, FeaturedProducts },
}: FeaturedProductsProps) => {
    const { rootGrants } = useAppData();
    const canEditAdmin = rootGrants.editProfile;
    const appDisplayMode = useAppDisplayModeContext();
    const { css } = useStyles();
    const [searchParams] = useSearchParams();

    const navigation = useNavigation();
    const { favouriteItem } = useSaveToDesignBoard('ProductPin');
    const tracker = useTracker();
    const {
        itemID: itemIDToDelete,
        cancelDelete,
        willDelete,
    } = useDeleteItemWithConfirm();

    const fetcher = useFetcher<never>();

    const unlinkItem = async (ChildID: number) => {
        setRemoveLoading(true);
        const data = { childID: `${ChildID}`, parentID: `${parentID}` };
        await fetcher.submit(data, {
            method: 'post',
            action: '/remix-api/project/unlink-library-item',
        });
    };

    const { edges, pageInfo } = FeaturedProducts;

    const [removeLoading, setRemoveLoading] = useState(false);

    useEffect(() => {
        if (fetcherIsDone(fetcher) && removeLoading === true) {
            cancelDelete();
            setRemoveLoading(false);
        }
    }, [fetcher, cancelDelete, removeLoading]);

    const [pinErrorMessage, setPinErrorMessage] = useState<string | null>(null);
    const { toggleHomePin } = useToggleHomePin(canEditAdmin, (error) => {
        if (error.Message) {
            setPinErrorMessage(error.Message);
        }
    });

    if (!edges?.length) return null;

    const totalCount = pageInfo.totalCount ?? 0;

    const showingAll = !!searchParams.get(SHOW_ALL_PARAM);
    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const featuredShowAll = nextSearchParams.get(SHOW_ALL_PARAM) === '1';
    const isLoading = navigation.state === 'loading' && featuredShowAll;

    return (
        <div className={css(S.ProductsUsedWrapper)}>
            <Header variant={'03'} className={css(S.SectionHeader)}>
                Products used in
                <br />
                {Title}
            </Header>
            <Grid columns={ROW_SIZE}>
                {edges.map(({ node: product }) => {
                    const hotButtons = getHotButtons({
                        item: product,
                        itemType: 'product',
                        appDisplayMode,
                        canEditAdmin,
                        canEdit: Boolean(product.canEdit),
                        onEditClick: visitBMEditor,
                        onUnlinkItemClick: () =>
                            willDelete(product.ID as number),
                        onRepositionClick: (item) => {
                            visitBMEditor(item, false, {
                                repositionThumb: true,
                            });
                        },
                        pinned: !!product.admin?.homePinned,
                        onPinClick: canHomePin(rootGrants)
                            ? () => {
                                  if (!product.admin) {
                                      return;
                                  }
                                  toggleHomePin(
                                      Number(product.ID),
                                      !product.admin.homePinned
                                  );
                              }
                            : undefined,
                    });
                    const adminCaption = getAdminCaption({
                        appDisplayMode,
                        item: product.admin,
                        canEditAdmin,
                    });
                    return (
                        <ProductTile
                            key={`featured-product-tile-${product.ID}`}
                            data={{
                                Product: product,
                                Professional: product.Professional,
                            }}
                            size="large"
                            favouriteItem={favouriteItem}
                            onClick={() => {
                                tracker.log('ProductUsedProjectClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData: product.ID,
                                    },
                                });
                            }}
                            topActions={
                                hotButtons ? (
                                    <HotButtons items={hotButtons} />
                                ) : undefined
                            }
                            adminCaption={
                                adminCaption ? (
                                    <AdminCaption adminCaption={adminCaption} />
                                ) : undefined
                            }
                        />
                    );
                })}
            </Grid>
            {!showingAll && totalCount > ROW_SIZE && (
                <Flex hAlign="start">
                    <Button
                        as={Link}
                        to={`?${SHOW_ALL_PARAM}=1`}
                        preventScrollReset={true}
                        loading={isLoading}
                        prefetch="intent"
                        className={css(S.LoadMoreButton)}
                        size={16}
                        outlined={true}
                        style={{
                            textTransform: isLoading ? 'unset' : 'uppercase',
                        }}
                        onClick={() => {
                            tracker.log('ProjectProductsExpandClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                        }}
                    >
                        {!isLoading ? `View more` : 'Loading...'}
                    </Button>
                </Flex>
            )}
            {favouriteItem.target && <SaveToDesignBoard {...favouriteItem} />}
            {(itemIDToDelete || removeLoading) && (
                <DeleteItemConfirmModal
                    title={'Remove product from project'}
                    content={
                        'Are you sure you would like to remove this product from the project?'
                    }
                    open={true}
                    onCancel={cancelDelete}
                    onConfirm={() => unlinkItem(itemIDToDelete as number)}
                    loading={removeLoading}
                />
            )}
            {pinErrorMessage && (
                <MessageModal
                    title={'Warning'}
                    content={pinErrorMessage}
                    open={true}
                    onConfirm={() => setPinErrorMessage(null)}
                />
            )}
        </div>
    );
};

export default FeaturedProducts;
